import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStarterContext } from '@/context/StarterContext';
import { Button } from '@/components/ui/Button';
import styles from '@/styles/components/WelcomeScreen.module.css';

type WelcomeStep = 'choose' | 'import-existing';

export function WelcomeScreen() {
  const navigate = useNavigate();
  const { createStarter } = useStarterContext();
  const [step, setStep] = useState<WelcomeStep>('choose');
  const [lastFedDate, setLastFedDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateNew = () => {
    navigate('/wizard');
  };

  const handleImportExisting = async () => {
    if (!lastFedDate || isCreating) return;
    setIsCreating(true);
    try {
      const fedDate = new Date(lastFedDate).toISOString();
      await createStarter(fedDate, 'My Starter');
      // StarterContext will refresh and HomeView will re-render to show Dashboard
    } catch {
      setIsCreating(false);
    }
  };

  if (step === 'import-existing') {
    return (
      <div className={styles.container}>
        <span className={styles.icon} aria-hidden="true">
          🍞
        </span>
        <h1 className={styles.title}>Import Your Starter</h1>
        <p className={styles.subtitle}>When did you last feed your starter?</p>

        <div className={styles.importForm}>
          <label className={styles.importLabel} htmlFor="lastFedDate">
            Last feeding date
          </label>
          <input
            id="lastFedDate"
            type="date"
            className={styles.importInput}
            value={lastFedDate}
            onChange={(e) => setLastFedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
          <Button
            onClick={handleImportExisting}
            disabled={!lastFedDate || isCreating}
          >
            {isCreating ? 'Setting up...' : 'Start Tracking'}
          </Button>
          <button
            className={styles.backButton}
            onClick={() => setStep('choose')}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <span className={styles.icon} aria-hidden="true">
        🌾
      </span>
      <h1 className={styles.title}>Welcome to Yeastus Crust!</h1>
      <p className={styles.subtitle}>Crust has risen.</p>
      <p className={styles.subtitle}>
        Track your sourdough starter and never forget a feeding again.
      </p>
      <p className={styles.question}>Do you have a starter?</p>

      <div className={styles.buttons}>
        <Button onClick={handleCreateNew}>🧪 Create a Starter</Button>
        <Button variant="secondary" onClick={() => setStep('import-existing')}>
          🍞 I Have a Starter
        </Button>
      </div>
    </div>
  );
}
